import React, { useMemo, useState } from "react";

import { useSelector } from "react-redux";

import { Button } from "components/Form";
import IdConnectedList from "components/IdConnectedList";
import { Column, Row } from "components/Layout";
import { Mobile2x2Layout } from "components/Layout/ContentHeader";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import MessageBox from "components/MessageBox";
import { SearchHeader } from "components/SearchInput/GlobalSearch";

import { ApiModel } from "constants/loading";

import { isWholePositiveNumber } from "lib/math";
import { openImportAuctionsPlusCSV } from "lib/navigation";

import {
  getCurrentSale,
  getKeywordLookups,
  selectAlphabeticalBuyerIds,
  selectBusinessIdByRegistrationNumberLookup,
} from "selectors";

import { BuyerCard } from "./BuyerCard";

function EmptyBuyers() {
  return <MessageBox>No Lots sold yet.</MessageBox>;
}

export const BuyersComponent = () => {
  const sale = useSelector(getCurrentSale);

  const buyerIds = useSelector(selectAlphabeticalBuyerIds);
  const [searchText, setSearchText] = useState("");

  function onBasicSearchTextChanged(searchText) {
    setSearchText(searchText);
  }
  const businessKeywordLookup = useSelector(getKeywordLookups).businesses;
  const isUsingRegisteredBidders =
    useSelector(getCurrentSale)?.using_registered_bidders;
  const businessIdByRegistrationNumberLookup = useSelector(
    selectBusinessIdByRegistrationNumberLookup,
  );

  const searchPlaceholder = isUsingRegisteredBidders
    ? "Bidder number/Buyer"
    : "Quicksearch";

  const filteredIds = useMemo(() => {
    if (isUsingRegisteredBidders && isWholePositiveNumber(searchText)) {
      return buyerIds.filter(
        businessId =>
          businessId === businessIdByRegistrationNumberLookup[searchText],
      );
    } else {
      const matchingBusinessIdsLookup = Object.keys(
        businessKeywordLookup,
      ).reduce((acc, lookupToken) => {
        if (lookupToken.includes(searchText.toLowerCase())) {
          businessKeywordLookup[lookupToken].forEach(b => {
            acc[b.business.id] = true;
          });
        }
        return acc;
      }, {});

      return buyerIds.filter(id => matchingBusinessIdsLookup[id]);
    }
  }, [
    isUsingRegisteredBidders,
    searchText,
    buyerIds,
    businessIdByRegistrationNumberLookup,
    businessKeywordLookup,
  ]);

  return (
    <Column>
      <SearchHeader
        showBasicSearch
        showFacetedSearch={false}
        onBasicSearchTextChanged={onBasicSearchTextChanged}
        placeholderText={searchPlaceholder}
        layout={Mobile2x2Layout}
      />
      <Row padding={1}>
        <Button
          data-tour="importAuctionsPlusResults"
          onClick={() => openImportAuctionsPlusCSV(sale)}
        >
          Import AuctionsPlus Results
        </Button>
      </Row>
      <IdConnectedList
        component={BuyerCard}
        orderedIds={filteredIds}
        emptyComponent={EmptyBuyers}
      />
    </Column>
  );
};

// Wait For Sync Wrapper
export const Buyers = () => {
  return (
    <WaitForSync
      requiredData={[
        ApiModel.SALE_LOTS,
        ApiModel.CONSIGNMENTS,
        ApiModel.BUSINESSES,
      ]}
    >
      <BuyersComponent />
    </WaitForSync>
  );
};
